// FASE 05 — Cadastros Gerais, Referências (plano de 23/09/2026, Lote C).
// Mockup de 11/08: nome, logo, linhas vinculadas e ClassesIds vinculadas. Decisões do PO:
//  - P9/P12: tipo de produto obrigatório (pelo menos um), com as 5 opções do legado;
//  - pergunta final (opção A): a caixa "Gerar no pacote da referência" de cada linha vinculada é o
//    único controle do pacote N1 e do bloqueio de linha enviado ao CEM MOBI.
import { useEffect, useMemo, useState } from "react";
import { Box, Grid, Paper } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { debounce } from "lodash";
import NavigationHeader from "components/NavigationHeader";
import { StripedDataGrid } from "components/StripedDataGrid";
import { FloatMenuCrud } from "components/FloatMenuCrud";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { TitleTable } from "components/TitleTable";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import { DashboardStackCustom, StackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import { TIME_DEBOUNCE } from "hooks/usePagination";
import { excluirCadastro, listarCadastro } from "services/modules/cadastrosGerais";
import { ModalReferencia } from "./ModalReferencia";

export interface IReferenciaListagem {
  idReferencia: number;
  descricao: string;
  logo?: string;
}

export const Referencias = () => {
  const { openNotification } = useNavigation();
  const [linhas, setLinhas] = useState<IReferenciaListagem[]>([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [paginacao, setPaginacao] = useState({ page: 1, pageSize: 10, search: "" });
  const [modal, setModal] = useState<{ aberto: boolean; idReferencia?: number }>({ aberto: false });
  const [exclusao, setExclusao] = useState<{ aberto: boolean; id?: number }>({ aberto: false });
  const [excluindo, setExcluindo] = useState(false);

  const carregar = async () => {
    setCarregando(true);
    try {
      const pagina = await listarCadastro<IReferenciaListagem>("/Referencia", paginacao);
      setLinhas(pagina.itens);
      setTotal(pagina.total);
    } catch (error: any) {
      openNotification({ message: error?.message || "Não foi possível carregar as referências", type: "error" });
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginacao]);

  const filtrar = useMemo(
    () => debounce((texto: string) => setPaginacao((atual) => ({ ...atual, page: 1, search: texto })), TIME_DEBOUNCE),
    []
  );

  const excluir = async () => {
    if (!exclusao.id) return;
    setExcluindo(true);
    try {
      await excluirCadastro("/Referencia", "idReferencia", exclusao.id);
      openNotification({ message: "Referência excluída com sucesso!", type: "success" });
      carregar();
    } catch (error: any) {
      openNotification({ message: error?.message || "Não foi possível excluir a referência", type: "error" });
    } finally {
      setExcluindo(false);
      setExclusao({ aberto: false });
    }
  };

  const colunas: GridColDef[] = [
    {
      // Plano 02/10/2026 (Frente 6): o Avatar (40x40, recorte "cover") cortava as logos largas
      // (KAWNEER, ALCOA, ALUMICONTE). Caixa 120x56 com a imagem inteira, como a logo do Projetista.
      field: "logo",
      headerName: "",
      sortable: false,
      width: 140,
      renderCell: (params) =>
        params.row.logo ? (
          <Box
            sx={{
              width: 120,
              height: 56,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={params.row.logo}
              alt=""
              sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          </Box>
        ) : null,
    },
    {
      field: "descricao",
      headerName: "Referência",
      sortable: false,
      flex: 1,
      renderHeader: () => (
        <FilterColumn label="Referência" name="descricao" onChange={(evento) => filtrar(evento.target.value)} />
      ),
    },
    {
      field: "acoes",
      headerName: "Ações",
      sortable: false,
      align: "center",
      headerAlign: "center",
      flex: 0.15,
      renderCell: (params) => (
        <FloatMenuCrud
          onEdit={() => setModal({ aberto: true, idReferencia: params.row.idReferencia })}
          showDuplicateOption={false}
          deleteButtonText="Excluir"
          onDelete={() => setExclusao({ aberto: true, id: params.row.idReferencia })}
        />
      ),
    },
  ];

  return (
    <DashboardStackCustom>
      <NavigationHeader
        title="CADASTRO DE REFERÊNCIAS"
        label="Referências"
        buttonLabel="Cadastrar"
        buttonOnClick={() => setModal({ aberto: true })}
        hasBackButton={false}
      />
      <Grid container display="flex" alignItems="center">
        <StackCustom>
          <Grid container mb={4}>
            <Grid item xs={12}>
              <TitleTable title="Listagem de referências do MOF" />
            </Grid>
          </Grid>
          <Paper>
            <StripedDataGrid
              rows={linhas}
              columns={colunas}
              getRowId={(linha: IReferenciaListagem) => linha.idReferencia}
              page={paginacao.page - 1}
              pageSize={paginacao.pageSize}
              rowCount={total}
              loading={carregando || excluindo}
              onPageChange={(pagina) => setPaginacao((atual) => ({ ...atual, page: pagina + 1 }))}
              onPageSizeChange={(tamanho) => setPaginacao((atual) => ({ ...atual, pageSize: tamanho, page: 1 }))}
              checkboxSelection={false}
              rowHeight={70}
            />
          </Paper>
        </StackCustom>
      </Grid>

      <ModalReferencia
        aberto={modal.aberto}
        idReferencia={modal.idReferencia}
        onFechar={(houveAlteracao) => {
          setModal({ aberto: false });
          if (houveAlteracao) carregar();
        }}
      />

      <ConfirmDeleteModal
        title="Tem certeza que deseja excluir a referência?"
        description="Essa operação não pode ser desfeita."
        open={exclusao.aberto}
        onClose={() => setExclusao({ aberto: false })}
        onConfirm={excluir}
        isLoadingDelete={excluindo}
      />
    </DashboardStackCustom>
  );
};
